import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useLayoutEffect, useState } from 'react'

import { useTRPC } from '@/lib/trpc'
import type { GameStatusData } from '@/lib/types'
import {
  createRoleActionLifecycle,
  isSameOwnedAction,
  type AcceptedRoleActionReceipt,
  type OwnedRoleAction,
  type RoleActionAttemptDependencies,
} from '@/lib/role-action-lifecycle'

interface RoleActionStatus {
  isError: boolean
  refetch: () => Promise<unknown>
}

interface RoleActionLifecycleOptions<
  TInput extends OwnedRoleAction,
  TDraft,
  TReceipt,
> {
  gameId: string
  input: TInput | null
  status: RoleActionStatus
  readInput: (data: GameStatusData) => TInput | null
  prepare: RoleActionAttemptDependencies<TInput, TDraft, TReceipt>['prepare']
  onAccepted?: (input: TInput, receipt: TReceipt) => void
  onReceiptChange?: (receipt: TReceipt | null, previous?: TReceipt) => void
  initialReceipt?: AcceptedRoleActionReceipt<TReceipt> | null
}

export function useRoleActionLifecycle<
  TInput extends OwnedRoleAction,
  TDraft,
  TReceipt,
>(options: RoleActionLifecycleOptions<TInput, TDraft, TReceipt>) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { mutateAsync: submitActionAsync } = useMutation(
    trpc.playRouter.submitAction.mutationOptions(),
  )
  const [, setRevision] = useState(0)
  const [runtime] = useState(() => {
    let currentOptions = options
    let currentSubmit = submitActionAsync
    const lifecycle = createRoleActionLifecycle<TInput, TDraft, TReceipt>({
      initialGameId: options.gameId,
      initialInput: options.input,
      initialReceipt: options.initialReceipt,
      initialReceiptCleanup: () => options.onReceiptChange?.(
        null,
        options.initialReceipt?.value,
      ),
      snapshot: () => {
        const attemptOptions = currentOptions
        const attemptSubmit = currentSubmit
        const statusQuery = trpc.playRouter.status.queryOptions({
          gameId: attemptOptions.gameId,
        })
        const creditQuery = trpc.userRouter.getUserInfo.queryOptions()
        return {
          readStatus: () => queryClient.fetchQuery({
            ...statusQuery,
            staleTime: 0,
          }),
          readCredit: async () => Number(
            (await queryClient.fetchQuery({
              ...creditQuery,
              staleTime: 0,
            })).bankCredit,
          ),
          readInput: attemptOptions.readInput,
          prepare: attemptOptions.prepare,
          submit: responses => attemptSubmit({
            gameId: attemptOptions.gameId,
            responses,
          }),
          converge: attemptOptions.status.refetch,
          onAccepted: attemptOptions.onAccepted,
          onReceiptChange: attemptOptions.onReceiptChange,
        }
      },
      onChange: () => setRevision(value => value + 1),
    })
    return {
      lifecycle,
      update(
        nextOptions: RoleActionLifecycleOptions<TInput, TDraft, TReceipt>,
        nextSubmit: typeof submitActionAsync,
      ) {
        currentOptions = nextOptions
        currentSubmit = nextSubmit
        lifecycle.replaceContext(nextOptions.gameId, nextOptions.input)
      },
    }
  })

  useLayoutEffect(() => runtime.update(options, submitActionAsync), [
    options,
    runtime,
    submitActionAsync,
  ])
  const { lifecycle } = runtime
  const state = lifecycle.getState()
  const context = lifecycle.getContext()
  const contextMatches = context.gameId === options.gameId
    && (options.input
      ? isSameOwnedAction(options.input, context.action)
      : !!state.receipt)
  const receiptMatchesInput = !options.input
    || !state.receipt
    || isSameOwnedAction(options.input, state.receipt)
  const visibleReceipt = contextMatches && receiptMatchesInput
    ? state.receipt
    : null
  const notice = contextMatches && receiptMatchesInput ? state.notice : 'none'

  return {
    blocked: lifecycle.blocked(options.input, options.status.isError),
    confirm: (draft: TDraft) =>
      lifecycle.confirm(options.input, draft, options.status.isError),
    edit: lifecycle.edit,
    locked: !!options.input && isSameOwnedAction(options.input, visibleReceipt),
    notice,
    receipt: visibleReceipt?.value ?? null,
    refresh: (afterFailure = false) =>
      lifecycle.refresh(options.input, afterFailure),
    clearReceipt: lifecycle.clearReceipt,
  }
}
